import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { getProfileChecklist } from "../../utils/profileCompleteness";
import "./ProfileChecklist.css";

// onNavigate(navTarget) is called when the user clicks a pending item with
// an in-page target — navTarget is { tab, subTab? }. Items whose nav is a
// full route instead ({ href }) navigate directly, since they live outside
// this tab structure entirely (e.g. cover image, edited from the public
// profile preview page).
const ProfileChecklist = ({ user, onNavigate }) => {
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const checklist = getProfileChecklist(user);

  if (!checklist || checklist.percent === 100) return null;

  const { percent, items } = checklist;
  const pending = items.filter((i) => !i.done);

  const handleItemClick = (item) => {
    if (item.nav?.href) navigate(item.nav.href);
    else onNavigate?.(item.nav);
  };

  // Matches the "89% complete: complete KYC to reach 100%" framing exactly
  // when there's one clear next step (every item label is already an
  // imperative phrase — "Add a profile photo", "Complete KYC verification"
  // — so it reads naturally appended as-is). Falls back to a count once
  // there's more than one, since naming all of them in a single line gets
  // unreadable.
  const subheading =
    pending.length === 1
      ? `${pending[0].label} to reach 100%.`
      : `${pending.length} steps left — finish these to get more visibility and bookings.`;

  return (
    <div className="profile-checklist-card">
      <div className="profile-checklist-header">
        <div className="profile-checklist-heading">
          <h3>Your profile is {percent}% complete</h3>
          <p>{subheading}</p>
        </div>
        <button
          type="button"
          className="profile-checklist-toggle"
          onClick={() => setCollapsed((c) => !c)}
          aria-label={collapsed ? "Show checklist" : "Hide checklist"}
        >
          {collapsed ? "Show" : "Hide"}
        </button>
      </div>

      <div className="profile-checklist-bar">
        <div
          className="profile-checklist-bar-fill"
          style={{ width: `${percent}%` }}
        />
      </div>

      {!collapsed && (
        <ul className="profile-checklist-items">
          {pending.map((item) => (
            <li key={item.key}>
              {item.nav ? (
                <button
                  type="button"
                  className="profile-checklist-item-btn"
                  onClick={() => handleItemClick(item)}
                >
                  <span className="profile-checklist-dot" />
                  {item.label}
                </button>
              ) : (
                <span className="profile-checklist-item-static">
                  <span className="profile-checklist-dot" />
                  {item.label}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default ProfileChecklist;
